<template>
    <div>
        <SmartBreadcrumbs
            type="InertiaLink"
            :pages="[
                {
                    name: $tc('messages.meeting_turns', 2),
                    href: null,
                },
                {
                    name: $t('messages.calendar'),
                    href: null,
                },
            ]"
        />
        <div>
            <form :action="meta.links.index" method="GET" @submit.prevent="filter" class="p-4" id="calendar_form">
                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.groups')" for="group_ids" />
                            <SlideOverCheckboxInput v-model="form.group_ids" :options="options.groups">
                                <div class="flex justify-end">
                                    <div class="mt-1">
                                        <SubmitFormButton
                                            form="calendar_form"
                                            :button-text="$t('messages.filter')"
                                            :processing="form.processing"
                                            is-filter
                                        />
                                    </div>
                                </div>
                            </SlideOverCheckboxInput>
                            <FormError :value="form.errors.group_ids" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                        <div class="flex justify-end space-x-2">
                            <SubmitFormButton
                                :button-text="$t('messages.filter')"
                                :processing="form.processing"
                                is-filter
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="w-full flex space-x-4 justify-between items-center" v-if="meta.can.create">
            <header class="flex items-center gap-x-4 justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
                <InertiaLink
                    :href="meta.links.index_previous_month"
                    class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                >
                    <span class="sr-only">Previous month</span>
                    <IconChevronLeft class="h-5 w-5" aria-hidden="true" />
                </InertiaLink>
                <h1 class="flex text-base font-semibold leading-6 text-gray-900">
                    <div class="flex-auto text-sm font-semibold">{{ calendar.month_name }} ({{ calendar.year }})</div>
                </h1>
                <InertiaLink
                    :href="meta.links.index_next_month"
                    class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
                >
                    <span class="sr-only">Next month</span>
                    <IconChevronRight class="h-5 w-5" aria-hidden="true" />
                </InertiaLink>
            </header>

            <AppButton element="InertiaLink" desired-action :href="meta.links.create">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div class="lg:flex lg:h-full lg:flex-col">
            <div class="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
                <div
                    class="grid grid-cols-5 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none"
                >
                    <div class="flex justify-center bg-white py-2">
                        <span>{{ $t('messages.monday_short') }}</span>
                    </div>
                    <div class="flex justify-center bg-white py-2">
                        <span>{{ $t('messages.tuesday_short') }}</span>
                    </div>
                    <div class="flex justify-center bg-white py-2">
                        <span>{{ $t('messages.wednesday_short') }}</span>
                    </div>
                    <div class="flex justify-center bg-white py-2">
                        <span>{{ $t('messages.thursday_short') }}</span>
                    </div>
                    <div class="flex justify-center bg-white py-2">
                        <span>{{ $t('messages.friday_short') }}</span>
                    </div>
                </div>
                <div class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
                    <div class="hidden w-full lg:grid lg:grid-cols-5 lg:gap-px">
                        <!--
                          Always include: "relative py-2 px-3"
                          Is current month, include: "bg-white"
                          Is not current month, include: "bg-gray-50 text-gray-500"
                        -->

                        <div
                            class="relative px-3 py-2"
                            :class="{
                                'bg-white': day.is_current_month,
                                'bg-gray-50 text-gray-500': !day.is_current_month,
                            }"
                            v-for="day in calendar.days"
                            :key="day.date"
                        >
                            <!--
                              Is today, include: "flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white"
                            -->
                            <time
                                :datetime="day.date"
                                :class="{
                                    'flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white':
                                        day.is_today,
                                }"
                            >
                                {{ day.day }}
                            </time>
                            <ol class="mt-2">
                                <li v-for="item in day.items" :key="item.id">
                                    <button
                                        @click="openTurn(item)"
                                        class="sm:flex justify-between group overflow-y-auto rounded-lg p-2 text-xs leading-5 mt-1 w-full"
                                        :class="{
                                            'bg-blue-50 hover:bg-blue-100': item.status_color === 'blue',
                                            'bg-green-50 hover:bg-green-100': item.status_color === 'green',
                                            'bg-gray-50 hover:bg-gray-100': item.status_color === 'gray',
                                        }"
                                    >
                                        <div class="sm:flex justify-between gap-x-1">
                                            <p
                                                class="flex-auto truncate font-medium text-gray-900 group-hover:text-indigo-600"
                                            >
                                                {{ item.group.name }}
                                            </p>
                                            <div v-if="item.status === 'reserved'">
                                                <Tippy>
                                                    <div>
                                                        <IconCalendarEvent class="w-4 h-4 text-blue-400 inline-block" />
                                                        <IconMessage
                                                            class="w-4 h-4 text-blue-400 inline-block"
                                                            v-if="item.comment"
                                                        />
                                                    </div>
                                                    <template #content>
                                                        <div class="mt-1" v-if="item.requester_id">
                                                            {{ $t('messages.requested_by') }}:
                                                            <div class="pl-2 lg:pl-4 font-light">
                                                                {{ item.requester_name }}
                                                            </div>
                                                        </div>
                                                        <div class="mt-1">
                                                            {{ $t('messages.student') }}:
                                                            <div class="pl-2 lg:pl-4 font-light">
                                                                {{ item.student_name }}
                                                            </div>
                                                        </div>
                                                        <div class="mt-1">
                                                            {{ $tc('messages.channels', 1) }}:
                                                            <div class="pl-2 lg:pl-4 font-light">
                                                                {{ item.channel_display }}
                                                            </div>
                                                        </div>
                                                        <div v-if="item.comment">
                                                            <div>{{ $t('messages.comment') }}:</div>
                                                            <div class="pl-2 lg:pl-4 font-light">
                                                                <p>{{ item.comment }}</p>
                                                            </div>
                                                        </div>
                                                    </template>
                                                </Tippy>
                                            </div>
                                        </div>
                                        <time
                                            :datetime="day.datetime"
                                            class="ml-3 flex-none text-gray-500 group-hover:text-indigo-600 block"
                                        >
                                            {{ item.time_start }} - {{ item.time_end }}
                                        </time>
                                    </button>
                                </li>
                            </ol>
                        </div>
                    </div>
                    <div class="isolate grid w-full grid-cols-5 gap-px lg:hidden">
                        <!--
                          Always include: "flex h-14 flex-col py-2 px-3 hover:bg-gray-100 focus:z-10"
                          Is current month, include: "bg-white"
                          Is not current month, include: "bg-gray-50"
                          Is selected or is today, include: "font-semibold"
                          Is selected, include: "text-white"
                          Is not selected and is today, include: "text-indigo-600"
                          Is not selected and is current month, and is not today, include: "text-gray-900"
                          Is not selected, is not current month, and is not today: "text-gray-500"
                        -->
                        <!--                        :class="{-->
                        <!--                        'bg-white': day.is_current_month,-->
                        <!--                        'bg-gray-50': !day.is_current_month,-->
                        <!--                        'text-white': day.date === selected,-->
                        <!--                        'text-indigo-600': !(day.date === selected) && day.is_today,-->
                        <!--                        'text-gray-900': !(day.date === selected) && !day.is_today && day.is_current_month,-->
                        <!--                        'text-gray-500': !(day.date === selected) && day.is_today,-->
                        <!--                        }"-->

                        <button
                            @click="selectDay(day)"
                            type="button"
                            class="flex h-14 flex-col py-2 px-3 hover:bg-gray-100 focus:z-10"
                            :class="[
                                !day.is_current_month && day.count === 0 ? 'bg-gray-50' : '',
                                !day.is_current_month && day.count > 0 ? 'bg-gray-100' : '',
                                day.is_current_month && day.count === 0 ? 'bg-white' : '',
                                day.is_current_month && day.count > 0 ? 'bg-indigo-100' : '',
                                (day.date === selected || day.is_today) && 'font-semibold',
                                day.date === selected && 'text-white',
                                !(day.date === selected) && day.is_current_month && !day.is_today && 'text-gray-900',
                                !(day.date === selected) && !day.is_current_month && !day.is_today && 'text-gray-400',
                                day.is_today && !(day.date === selected) && 'text-indigo-600',
                                dayIdx === 0 && 'rounded-tl-lg',
                                dayIdx === 4 && 'rounded-tr-lg',
                                dayIdx === calendar.days.length - 5 && 'rounded-bl-lg',
                                dayIdx === calendar.days.length - 1 && 'rounded-br-lg',
                            ]"
                            v-for="(day, dayIdx) in calendar.days"
                            :key="day.date"
                        >
                            <!--
                          Always include: "ml-auto"
                          Is selected, include: "flex h-6 w-6 items-center justify-center rounded-full"
                          Is selected and is today, include: "bg-indigo-600"
                          Is selected and is not today, include: "bg-gray-900"
                        -->
                            <time
                                :datetime="day.date"
                                class="ml-auto"
                                :class="{
                                    'bg-gray-900': day.date === selected && !day.is_today,
                                    'bg-indigo-600': day.date === selected && day.is_today,
                                    'flex h-6 w-6 items-center justify-center rounded-full': day.date === selected,
                                }"
                            >
                                {{ day.day }}
                            </time>
                            <span class="sr-only">
                                {{ day.items.length }} {{ $tc('messages.meeting_turns', day.items.length) }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-auto-animate>
        <ol class="mt-2 block lg:hidden" v-if="selectedDay">
            <li v-for="item in selectedDay.items ?? []" :key="item.id">
                <button
                    @click="openTurn(item)"
                    class="flex justify-between group overflow-y-auto rounded-lg p-2 text-xs leading-5 mt-1 w-full"
                    :class="{
                        'bg-blue-50 hover:bg-blue-100': item.status_color === 'blue',
                        'bg-green-50 hover:bg-green-100': item.status_color === 'green',
                        'bg-gray-50 hover:bg-gray-100': item.status_color === 'gray',
                    }"
                >
                    <div class="flex justify-between gap-x-1">
                        <p class="flex-auto truncate font-medium text-gray-900 group-hover:text-indigo-600">
                            {{ item.group.name }}
                        </p>
                        <div v-if="item.status === 'reserved'">
                            <div>
                                <IconCalendarEvent class="w-4 h-4 text-blue-400 inline-block" />
                                <IconMessage class="w-4 h-4 text-blue-400 inline-block" v-if="item.comment" />
                            </div>
                        </div>
                    </div>
                    <time
                        :datetime="item.datetime"
                        class="ml-3 flex-none text-gray-500 group-hover:text-indigo-600 block"
                    >
                        {{ item.time_start }} - {{ item.time_end }}
                    </time>
                </button>
            </li>
        </ol>
    </div>
    <EditMeetingTurnSlideOver :turn="turn" :options="options" @close="turn = null" v-if="turn !== null" />
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import AppButton from '@/components/AppButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import { IconCalendarEvent, IconChevronLeft, IconChevronRight, IconMessage } from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import EditMeetingTurnSlideOver from '@/Pages/Teacher/MeetingTurns/Calendar/Partials/EditMeetingTurnSlideOver.vue';
import SmartBreadcrumbs from '@/components/SmartBreadcrumbs.vue';

export default {
    components: {
        IconChevronLeft,
        IconChevronRight,
        SmartBreadcrumbs,
        EditMeetingTurnSlideOver,
        Tippy,
        SubmitFormButton,
        FormLabel,
        FormError,
        SlideOverCheckboxInput,
        AppButton,
        IconMessage,
        IconCalendarEvent,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'calendar', 'options', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                group_ids: this.filters.group_ids,
            }),
            selected: this.filters.day_month,
            turn: null,
        };
    },
    computed: {
        selectedDay: function () {
            if (this.selected === null) {
                return null;
            }

            return this.calendar.days.find((d) => d.date === this.selected);
        },
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        selectDay(day) {
            this.selected = day.date;
        },
        openTurn(turn) {
            this.slideOverOpen = true;
            this.turn = turn;
        },
    },
};
</script>
